import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  CircleAlert,
  Info,
  Loader,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Notice from "../common/Notice";
import SectionLabel from "../common/SectionLabel";
import { XP_DISCLAIMER } from "../../config/supernovaXp";
import { SUPERNOVA_STAKING_ADDRESS } from "./supernovaContracts";
import { ConfirmRiskBlock, StakeRiskBanner } from "./SupernovaDisclosure";
import SupernovaOrb from "./SupernovaOrb";
import { fmtCount, fmtDate, fmtMultiplier, fmtXpExact, shortAddr } from "./supernovaFormat";
import buildStakePreview, {
  LOCK_CHIPS,
  stakeBlockedMessage,
} from "./supernovaStakePreview";
import useSupernovaStake, { APPROVAL, TX, isTxBusy } from "./useSupernovaStake";

/**
 * The stake modal — pick a type, a quantity and a lock, see what the model says,
 * then sign it in your own wallet.
 *
 * THREE THINGS THIS COMPONENT WILL NOT DO:
 *
 *  1. Compute. Every figure comes from supernovaStakePreview, which in turn
 *     reads config/supernovaXp. This file renders numbers, it never derives
 *     them, so the preview cannot drift from the engine's model.
 *
 *  2. Fake a success. If the service has no signing path in this build, the
 *     member gets a plainly-labelled "signing unavailable" panel — not a
 *     spinner that resolves into a stake that never happened.
 *
 *  3. Sign. Staking is a transaction the member signs in their own wallet.
 *     ARCrypto never holds a key, an NFT, or a signature, and this modal has no
 *     path that could.
 *
 * A DECLINED SIGNATURE IS NOT AN ERROR. The form lives in this component and
 * the transaction state lives in the hook, so "I changed my mind" resets the
 * machine and leaves every choice exactly where the member left it.
 *
 * COMPLIANCE: XP is an in-app points balance. Nothing on this surface is a
 * return, a yield, income, a price, or a projection, and the risk disclosure is
 * on screen the whole time the Stake button is. Do not add a figure with a
 * currency or a percent sign to this file.
 *
 * @param {Object} props
 * @param {boolean} [props.open]
 * @param {number} props.tokenId           the type the member clicked Stake on
 * @param {Array} props.tokens             summary.tokens — per-type wallet counts
 * @param {string|null} props.wallet       connected address (they sign, we don't)
 * @param {(info: {hash: string|null}) => void} props.onClose
 * @param {(result: Object) => void} [props.onStaked] fired once, on confirmation
 * @param {Object} [props.service]         injectable supernovaChain (tests)
 * @param {number} [props.nowMs]           injectable clock (tests)
 */
const SupernovaStakeModal = ({
  open = true,
  tokenId,
  tokens = [],
  wallet = null,
  onClose,
  onStaked,
  service,
  nowMs,
}) => {
  const [selectedId, setSelectedId] = useState(tokenId);
  const [qtyText, setQtyText] = useState("1");
  /* NO LOCK IS PRE-SELECTED. The lock is the one choice here that can't be
     undone once it's signed, so the member has to pick it themselves — until
     they do, the preview reads NO_LOCK and "Stake & sign" stays disabled. A
     pre-filled chip is how somebody signs a duration they never chose. */
  const [lockDays, setLockDays] = useState(null);

  const stake = useSupernovaStake(wallet, { enabled: open, service });
  const { approval, approvalTx, stakeTx } = stake;

  const row = tokens.find((t) => t.id === selectedId) || null;
  /* inWallet is null when the balance read isn't ready — which is NOT zero, and
     the preview refuses to offer a stake on a balance we can't confirm. */
  const maxQty = row && typeof row.inWallet === "number" ? row.inWallet : null;

  const qty = qtyText.trim() === "" ? NaN : Number(qtyText);
  const preview = useMemo(
    () =>
      buildStakePreview({
        tokenId: selectedId,
        qty,
        lockDays,
        maxQty,
        nowMs: nowMs ?? Date.now(),
      }),
    [selectedId, qty, lockDays, maxQty, nowMs]
  );

  /* One transaction at a time: the approval, then the stake. */
  const activeTx = stakeTx.state !== TX.IDLE ? stakeTx : approvalTx;
  const activeTxKind = stakeTx.state !== TX.IDLE ? "stake" : "approval";
  const inTxPhase = activeTx.state !== TX.IDLE;
  const busy = isTxBusy(activeTx.state);

  /* Confirmation is the only thing that tells the tab to refresh and toast. It
     fires once — a re-render can't replay it, because CONFIRMED is terminal. */
  useEffect(() => {
    if (stakeTx.state !== TX.CONFIRMED) return;
    onStaked?.({
      tokenId: preview.tokenId,
      tokenName: preview.tokenName,
      qty: preview.qty,
      lockDays: preview.lockDays,
      lockLabel: preview.lockLabel,
      multiplier: preview.multiplier,
      xpAtMaturity: preview.xpAtMaturity,
      slots: preview.slots,
      unlockAtMs: preview.unlockAtMs,
      hash: stakeTx.hash,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeTx.state]);

  const close = () => onClose?.({ hash: stakeTx.hash || approvalTx.hash || null });

  const handleType = (e) => {
    setSelectedId(Number(e.target.value));
    setQtyText("1");
  };

  /* While a wallet prompt is open or a transaction is in flight, Esc and the
     overlay do nothing — closing mid-signature would drop the state that tells
     the member (and the tab) what actually happened. */
  const dismiss = busy ? () => {} : close;

  return (
    <Modal open={open} onClose={dismiss} title="Stake Supernovas">
      <div className="text-left" data-testid="supernova-stake-modal">
        <p className="text-[12.5px] text-arc-ink-3 -mt-1 mb-4">
          Signed by your wallet · lock from 30 days to 3 years · longer locks
          accrue XP faster · each NFT unlocks 1 strategy slot
        </p>

        {inTxPhase ? (
          <TxPhase kind={activeTxKind} tx={activeTx} preview={preview} />
        ) : (
          <StakeForm
            tokens={tokens}
            selectedId={selectedId}
            onType={handleType}
            qtyText={qtyText}
            onQty={setQtyText}
            maxQty={maxQty}
            lockDays={lockDays}
            onLock={setLockDays}
            preview={preview}
            approval={approval}
          />
        )}

        <div className="mt-4">
          <StakeRiskBanner />
        </div>

        <Actions
          inTxPhase={inTxPhase}
          kind={activeTxKind}
          tx={activeTx}
          stake={stake}
          preview={preview}
          onClose={close}
        />
      </div>
    </Modal>
  );
};

/* ── The form ───────────────────────────────────────────────────────────── */

/* Static field ids — only one stake modal is ever mounted, and a stable id
   keeps the label association readable in the DOM. */
const TYPE_ID = "supernova-stake-type";
const QTY_ID = "supernova-stake-qty";

const StakeForm = ({
  tokens,
  selectedId,
  onType,
  qtyText,
  onQty,
  maxQty,
  lockDays,
  onLock,
  preview,
  approval,
}) => (
  <div className="space-y-[15px]">
    <Field label="Type" htmlFor={TYPE_ID}>
      <div className="flex items-center gap-3">
        <SupernovaOrb tokenId={selectedId} size={34} />
        <select
          id={TYPE_ID}
          value={selectedId ?? ""}
          onChange={onType}
          className="flex-1 min-w-0 rounded-arc-ctl bg-arc-paper-2 text-arc-ink text-[14px] px-3 py-2.5 ring-1 ring-arc-line focus:outline-none"
        >
          {tokens.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} —{" "}
              {typeof t.inWallet === "number"
                ? `${fmtCount(t.inWallet)} in wallet`
                : "wallet balance unavailable"}
            </option>
          ))}
        </select>
      </div>
    </Field>

    <Field label="Quantity" htmlFor={QTY_ID}>
      <div className="flex items-center gap-2">
        <input
          id={QTY_ID}
          type="number"
          inputMode="numeric"
          min={1}
          max={maxQty ?? undefined}
          step={1}
          value={qtyText}
          onChange={(e) => onQty(e.target.value)}
          className="tnum w-[110px] rounded-arc-ctl bg-arc-paper-2 text-arc-ink text-[14px] px-3 py-2.5 ring-1 ring-arc-line focus:outline-none"
        />
        {typeof maxQty === "number" && maxQty > 0 && (
          <button
            type="button"
            onClick={() => onQty(String(maxQty))}
            className="text-[12.5px] font-semibold text-arc-accent-ink underline underline-offset-2"
          >
            Max ({fmtCount(maxQty)})
          </button>
        )}
        <span className="text-caption text-arc-ink-3">
          {/* Staking moves the NFT into the contract, so the wallet balance IS
              the ceiling — a staked NFT is no longer yours to stake again. */}
          {typeof maxQty === "number"
            ? "in your wallet, free to stake"
            : "we can't read this balance right now"}
        </span>
      </div>
    </Field>

    <Field label="Lock duration">
      <div
        role="radiogroup"
        aria-label="Lock duration"
        className="flex flex-wrap gap-2"
      >
        {LOCK_CHIPS.map((chip) => {
          const on = chip.days === lockDays;
          return (
            <button
              key={chip.days}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onLock(chip.days)}
              className={[
                "flex-1 min-w-[78px] rounded-arc-ctl px-2 py-2 text-center transition ring-1",
                on
                  ? "bg-arc-accent-soft ring-arc-accent text-arc-ink"
                  : "bg-arc-paper-2 ring-arc-line text-arc-ink-2 hover:ring-arc-line-2",
              ].join(" ")}
            >
              <span className="block text-[13.5px] font-semibold tnum">
                {chip.chipLabel}
              </span>
              <span className="block text-[10.5px] text-arc-ink-3 tnum">
                {fmtMultiplier(chip.multiplier)}
                {chip.edge ? ` · ${chip.edge}` : ""}
              </span>
            </button>
          );
        })}
      </div>
    </Field>

    <XpPreview preview={preview} />
    <ApprovalStep approval={approval} />
  </div>
);

const Field = ({ label, htmlFor, children }) => (
  <div>
    <SectionLabel as={htmlFor ? "label" : "p"} htmlFor={htmlFor} className="block mb-1.5">
      {label}
    </SectionLabel>
    {children}
  </div>
);

/* ── Live preview ───────────────────────────────────────────────────────── */

/**
 * The headline is the model's CEILING for this lot held to term — labelled as
 * such, with the daily-accrual caption right under it, because "XP at full
 * maturity" read alone is the one line on this screen a member could mistake
 * for something being credited today.
 */
const XpPreview = ({ preview }) => {
  const blocked = stakeBlockedMessage(preview);
  return (
    <div
      className="rounded-[14px] bg-arc-paper-2 ring-1 ring-arc-line px-4 py-3.5"
      data-testid="supernova-stake-preview"
    >
      <SectionLabel>XP at full maturity</SectionLabel>
      <p className="text-hero-num text-arc-accent-ink tnum mt-0.5">
        {preview.valid ? `${fmtXpExact(preview.xpAtMaturity)} XP` : "—"}
      </p>
      <p className="text-caption text-arc-ink-3 mt-1">
        Accrues daily, pro-rata — nothing is credited up front. This is the
        model&apos;s maximum for this lock under today&apos;s parameters, not a
        forecast. {XP_DISCLAIMER}
      </p>

      {preview.valid ? (
        <>
          <p className="text-[11.5px] text-arc-ink-4 mt-2 tnum leading-[1.5]">
            {preview.formula}
          </p>
          <p className="text-[12.5px] text-arc-ink-2 mt-2 tnum">
            +{preview.slots === 1 ? "1 strategy slot" : `${fmtCount(preview.slots)} strategy slots`}{" "}
            · {fmtMultiplier(preview.multiplier)} rate for this lock · locked
            until {fmtDate(preview.unlockAtMs)}
          </p>
        </>
      ) : (
        <p className="text-[12.5px] text-arc-warn mt-2">{blocked}</p>
      )}
    </div>
  );
};

/* ── The one-time approval step, in plain language ──────────────────────── */

/**
 * ERC-1155 approval explained the way a member would ask about it: what it
 * lets happen, what it does not, and that it is once for the whole collection.
 * Shown BEFORE the first stake — never as a surprise second wallet prompt.
 */
const ApprovalStep = ({ approval }) => {
  if (approval.status === APPROVAL.LOADING || approval.status === APPROVAL.IDLE) {
    return (
      <p className="text-caption text-arc-ink-3 flex items-center gap-2">
        <Loader size={13} className="animate-spin" aria-hidden="true" />
        Checking whether your wallet has already approved the staking contract…
      </p>
    );
  }

  if (approval.status === APPROVAL.ERROR) {
    return (
      <Notice tone="warn" className="text-[13px]">
        <p className="font-semibold text-arc-warn mb-0.5">
          We couldn&apos;t check your approval
        </p>
        <p>
          {approval.message} Your wallet will still tell you exactly what it is
          being asked to sign — read the prompt before you approve it.{" "}
          <button
            type="button"
            onClick={approval.refresh}
            className="underline font-semibold text-arc-warn"
          >
            Check again
          </button>
        </p>
      </Notice>
    );
  }

  if (approval.approved === true) {
    return (
      <p
        className="text-[12.5px] text-arc-pos flex items-start gap-2"
        data-testid="supernova-approval-granted"
      >
        <Check size={15} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          Your wallet has already approved the staking contract, so this takes
          one signature instead of two. You can revoke that approval from your
          wallet at any time.
        </span>
      </p>
    );
  }

  return (
    <div
      className="rounded-[13px] bg-arc-card ring-1 ring-arc-line shadow-arc-sm p-4"
      data-testid="supernova-approval-explainer"
    >
      <div className="flex gap-3">
        <ShieldCheck size={18} className="flex-shrink-0 mt-0.5 text-arc-accent" aria-hidden="true" />
        <div className="text-[13px] leading-[1.55] text-arc-ink-2">
          <p className="font-semibold text-arc-ink mb-1">
            First stake: two signatures, not one
          </p>
          <p>
            Staking works by the staking contract taking the NFTs you choose out
            of your wallet and holding them for the lock. Before it can do that,
            your wallet has to give it permission — the standard ERC-1155
            &ldquo;approve for all&rdquo; step. You sign it once.
          </p>
          <ul className="mt-2 space-y-1.5 list-none">
            <li className="flex gap-2">
              <span className="text-arc-accent-ink" aria-hidden="true">
                1.
              </span>
              <span>
                <b className="text-arc-ink">Approve</b> — permission only. It
                moves nothing by itself and costs you no Supernovas. It applies
                to one contract: the Supernova staking contract on Arbitrum
                (&thinsp;<span className="tnum">{shortAddr(SUPERNOVA_STAKING_ADDRESS)}</span>&thinsp;).
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-arc-accent-ink" aria-hidden="true">
                2.
              </span>
              <span>
                <b className="text-arc-ink">Stake</b> — the transaction that
                actually locks the quantity you picked, for the duration you
                picked.
              </span>
            </li>
          </ul>
          <p className="text-caption text-arc-ink-3 mt-2">
            The approval covers the whole collection rather than one NFT, which
            is why you only do it once — and why you should revoke it from your
            wallet or a block explorer if you ever stop using the staking
            contract. It gives ARC Educational nothing: we never hold your keys,
            your NFTs, or your funds, and we cannot sign for you.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ── Transaction states ─────────────────────────────────────────────────── */

const KIND_LABEL = { approval: "Approval", stake: "Stake" };

/**
 * One panel for every outcome. The UNAVAILABLE branch is the important one: the
 * service has no signing path in this build, and this says exactly that instead
 * of pretending the stake went through.
 */
const TxPhase = ({ kind, tx, preview }) => {
  const what = KIND_LABEL[kind] || "Transaction";
  return (
    <div data-testid="supernova-stake-tx" data-tx-state={tx.state} className="space-y-3">
      {/* Only for the stake itself — an approval grants permission over the
          whole collection, so quoting a quantity and a lock against it would
          describe something the transaction doesn't do. */}
      {kind === "stake" && (
        <div className="rounded-[13px] bg-arc-paper-2 ring-1 ring-arc-line px-4 py-3">
          <SectionLabel>What you asked for</SectionLabel>
          <p className="text-[13.5px] text-arc-ink mt-0.5 tnum">
            {fmtCount(preview.qty)} × {preview.tokenName} · {preview.lockLabel} lock ·{" "}
            {fmtMultiplier(preview.multiplier)}
          </p>
          {/* The last thing read before a signature, so it says the three things
              that matter at that moment: who signs, that the exit is theirs, and
              the risk. Not a summary of the disclosure above — the sentence the
              member is confirming against. */}
          <ConfirmRiskBlock />
        </div>
      )}

      {tx.state === TX.PENDING && (
        <StatePanel
          icon={Loader}
          iconClass="text-arc-accent animate-spin"
          title={`${what}: waiting for your signature`}
          body={tx.message}
        />
      )}

      {tx.state === TX.SIGNED && (
        <StatePanel
          icon={Loader}
          iconClass="text-arc-accent animate-spin"
          title={`${what}: signed, awaiting confirmation`}
          body={tx.message}
          hash={tx.hash}
        />
      )}

      {tx.state === TX.CONFIRMED && (
        <StatePanel
          icon={Check}
          iconClass="text-arc-pos"
          tone="pos"
          title={`${what} confirmed on Arbitrum`}
          body={
            kind === "stake"
              ? `${fmtCount(preview.qty)} locked until ${fmtDate(preview.unlockAtMs)}. XP accrues daily from here — nothing was credited up front.`
              : "Permission granted. You can sign the stake itself now."
          }
          hash={tx.hash}
        />
      )}

      {tx.state === TX.REJECTED && (
        <StatePanel
          icon={Info}
          iconClass="text-arc-ink-3"
          title="You declined the signature"
          body={tx.message}
        />
      )}

      {tx.state === TX.UNAVAILABLE && (
        <div
          className="flex gap-3 rounded-[13px] bg-arc-paper-2 ring-1 ring-arc-line-2 p-4"
          data-testid="supernova-signing-unavailable"
        >
          <CircleAlert size={18} className="flex-shrink-0 mt-0.5 text-arc-ink-3" aria-hidden="true" />
          <div className="text-[13.5px] leading-[1.55] text-arc-ink-2">
            <p className="font-semibold text-arc-ink mb-1">
              Signing is unavailable in this build
            </p>
            <p>
              {/* TODO(backend, supervised): the staking contract's write path
                  ships under human supervision (supernovaChain.stake). Until it
                  does this is the end of the flow — and it must stay an honest
                  dead end rather than a fake confirmation. */}
              {kind === "stake"
                ? "This build can preview a stake but cannot submit one: "
                : "This build cannot send the approval transaction: "}
              {tx.message} <b>Nothing was signed and nothing changed on-chain.</b>{" "}
              Your Supernovas are still in your wallet and any existing stakes
              are untouched.
            </p>
            <p className="text-caption text-arc-ink-3 mt-2">
              To stake today, use the contract directly from your own wallet —
              this app never signs on your behalf either way.
            </p>
          </div>
        </div>
      )}

      {tx.state === TX.ERROR && (
        <StatePanel
          icon={TriangleAlert}
          iconClass="text-arc-neg"
          tone="neg"
          title={`${what} didn't go through`}
          body={tx.message}
        />
      )}
    </div>
  );
};

const TONES = {
  neutral: "bg-arc-paper-2 ring-arc-line",
  pos: "bg-arc-pos-soft ring-arc-line",
  neg: "bg-arc-neg-soft ring-arc-line",
};

const StatePanel = ({ icon: Icon, iconClass, tone = "neutral", title, body, hash }) => (
  <div className={`flex gap-3 rounded-[13px] ring-1 p-4 ${TONES[tone]}`}>
    <Icon size={18} className={`flex-shrink-0 mt-0.5 ${iconClass}`} aria-hidden="true" />
    <div className="text-[13.5px] leading-[1.55] text-arc-ink-2">
      <p className="font-semibold text-arc-ink mb-0.5">{title}</p>
      <p>{body}</p>
      {hash && (
        <p className="text-caption text-arc-ink-4 mt-1.5 tnum break-all">
          Transaction {shortAddr(hash)}
        </p>
      )}
    </div>
  </div>
);

/* ── Footer actions ─────────────────────────────────────────────────────── */

/**
 * What the primary button is depends on where the member is: approve first if
 * we know approval is missing, otherwise sign the stake. In a terminal state it
 * becomes the recovery action — and after a decline that recovery is a plain
 * "Try again" that drops straight back to the form with every choice intact.
 */
const Actions = ({ inTxPhase, kind, tx, stake, preview, onClose }) => {
  const { approval, approve, submit, resetStakeTx, resetApprovalTx } = stake;
  const retry = kind === "stake" ? resetStakeTx : resetApprovalTx;

  if (inTxPhase) {
    const busy = isTxBusy(tx.state);
    /* A confirmed APPROVAL is the halfway point, not the end: dropping the
       member back on the form is what lets them sign the stake itself. A
       confirmed STAKE is the end, and the tab takes it from there. */
    const approvalDone = tx.state === TX.CONFIRMED && kind === "approval";
    return (
      <Row>
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          {tx.state === TX.CONFIRMED && kind === "stake" ? "Done" : "Close"}
        </Button>
        {(tx.state === TX.REJECTED || tx.state === TX.ERROR) && (
          <Button onClick={retry}>Try again</Button>
        )}
        {approvalDone && <Button onClick={retry}>Continue to stake</Button>}
        {busy && (
          <span className="text-caption text-arc-ink-3">
            Your wallet is handling this — it can&apos;t be cancelled from here.
          </span>
        )}
      </Row>
    );
  }

  const needsApproval = approval.needed;
  const checking = approval.status === APPROVAL.LOADING || approval.status === APPROVAL.IDLE;

  return (
    <Row>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {needsApproval ? (
        <Button onClick={approve}>Approve collection &amp; continue</Button>
      ) : (
        <Button
          onClick={() =>
            submit({
              tokenId: preview.tokenId,
              qty: preview.qty,
              lockDays: preview.lockDays,
            })
          }
          disabled={!preview.valid || checking}
          title={preview.valid ? undefined : stakeBlockedMessage(preview) || undefined}
        >
          Stake &amp; sign
        </Button>
      )}
    </Row>
  );
};

const Row = ({ children }) => (
  <div className="flex items-center justify-end gap-2.5 flex-wrap mt-4">{children}</div>
);

export default SupernovaStakeModal;
